import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_ReferencePseudoClasses() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">✨ CSS Pseudo-Classes</h1>

      {/* Intro */}
      <p className="tutorial-intro" style={{ marginBottom: "30px" }}>
        CSS <strong>Pseudo-Classes</strong> allow you to style elements based on
        their state, position, or user interaction without adding extra classes
        or IDs.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>Pseudo-classes style elements based on state or position.</li>
          <li>
            Common interactive states: <code>:hover</code>, <code>:focus</code>,{" "}
            <code>:active</code>.
          </li>
          <li>
            Structural pseudo-classes: <code>:first-child</code>,{" "}
            <code>:last-child</code>, <code>:nth-child()</code>.
          </li>
          <li>UI feedback without extra markup.</li>
          <li>
            Combine pseudo-classes with regular selectors or combinators for
            advanced targeting.
          </li>
        </ul>
      </div>

      {/* 1️⃣ Hover */}
      <h2 className="section-title">1️⃣ :hover</h2>
      <p>Applies styles when the user hovers over an element.</p>
      <CodeCopyBlock
        language="css"
        code={`button:hover {
  background-color: #10b981;
  color: white;
}`}
      />
      <p style={{ marginBottom: "30px" }}>
        👉 Changes button color when hovered.
      </p>

      {/* 2️⃣ Focus */}
      <h2 className="section-title">2️⃣ :focus</h2>
      <p>Styles elements that are focused (like input fields).</p>
      <CodeCopyBlock
        language="css"
        code={`input:focus {
  border-color: #3b82f6;
  outline: none;
}`}
      />
      <p style={{ marginBottom: "30px" }}>
        👉 Input field border changes when selected.
      </p>

      {/* 3️⃣ :nth-child() */}
      <h2 className="section-title">3️⃣ :nth-child()</h2>
      <p>Selects elements based on their position among siblings.</p>
      <CodeCopyBlock
        language="css"
        code={`li:nth-child(odd) {
  background-color: #fef3c7;
}`}
      />
      <p style={{ marginBottom: "30px" }}>
        👉 Applies background to odd list items.
      </p>

      {/* 4️⃣ :first-child & :last-child */}
      <h2 className="section-title">4️⃣ :first-child & :last-child</h2>
      <p>Target the first or last element among siblings.</p>
      <CodeCopyBlock
        language="css"
        code={`p:first-child {
  font-weight: bold;
}
p:last-child {
  color: red;
}`}
      />
      <p style={{ marginBottom: "30px" }}>
        👉 First paragraph bold, last paragraph red.
      </p>

      {/* Example Structure with Internal CSS */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bfdbfe)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h3 style={{ color: "#1e3a8a" }}>
          📘 Example Structure (with Internal CSS):
        </h3>
        <CodeCopyBlock
          language="html"
          code={`<style>
button:hover {
  background-color: #10b981;
  color: white;
}
input:focus {
  border-color: #3b82f6;
  outline: none;
}
li:nth-child(odd) {
  background-color: #fef3c7;
}
p:first-child {
  font-weight: bold;
}
p:last-child {
  color: red;
}
</style>

<ul>
  <li>Item 1</li>
  <li>Item 2</li>
  <li>Item 3</li>
  <li>Item 4</li>
</ul>

<p>First paragraph</p>
<p>Middle paragraph</p>
<p>Last paragraph</p>

<button>Hover me</button>
<input placeholder="Focus me"/>`}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #dcfce7, #bbf7d0)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#166534" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Combine pseudo-classes with pseudo-elements like{" "}
            <code>::before</code> and <code>::after</code>.
          </li>
          <li>
            Experiment with <code>:not()</code>, <code>:empty</code>, and other
            advanced pseudo-classes.
          </li>
          <li>
            Use pseudo-classes for responsive or interactive UI without extra
            HTML markup.
          </li>
        </ul>
      </div>
    </div>
  );
}
